'use client'

import { useMemo, useState } from 'react'
import { useLowStock } from '@/components/menu/LowStockContext'
import { withLowStockTag } from '@/components/menu/LowStockBadge'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { localizedItem, type ItemTranslations } from '@/lib/menu/localizeItem'
import { localizedGroupLabel } from '@/lib/menu/localizedName'
import { guidedExtrasTitle } from '@/lib/menu/guidedExtrasTitle'
import type { Locale } from '@/lib/i18n/config'
import { SheetPortal } from './SheetPortal'
import { menuPhotoUrl } from '@/lib/utils/imageTransform'
import { focalPosition } from '@/lib/menu/photoFocal'
import { groupShowsPhotos, orderOptionsByPhoto } from '@/lib/menu/optionPhotoLayout'
import type { CartItem } from './types'
import { effectiveItemCardPrice, type PricingConfig } from '@/lib/utils/pricing'
import { useGuidedItemFlow } from '@/hooks/useGuidedItemFlow'
import { MAX_NOTE_LEN, allQuestionsSatisfied, firstUnsatisfiedQuestionIndex } from '@/lib/menu/guidedFlowReducer'
import { buildMicrositeCartLine } from '@/lib/microsite/buildMicrositeCartLine'
import type { ExtraOption, GuidedExtrasStep, GuidedQuestionStep, GuidedJoinRow } from '@/lib/menu/guidedSteps'

type Item = {
  id: string
  name: string
  description: string | null
  translations?: ItemTranslations | null
  price: number
  image_url: string | null
  image_focal?: string | null
  category_id?: string | null
  item_modifier_groups?: GuidedJoinRow[]
}

type Props = {
  item: Item
  pricingCfg: PricingConfig
  accentColor: string
  onAdd: (cartItem: CartItem) => void
  onClose: () => void
}

export function GuidedItemFlow({ item, pricingCfg, accentColor, onAdd, onClose }: Props) {
  const t = useTranslations('public.microsite.guided')
  const lowStock = useLowStock()
  const locale = (useLocale() === 'en' ? 'en' : 'es') as Locale
  const display = localizedItem(item, locale)
  const [noteExpanded, setNoteExpanded] = useState(false)

  // El microsite NO edita líneas del carrito desde acá, así que isEditing
  // queda en false y el auto-avance funciona siempre.
  const flow = useGuidedItemFlow({
    joinRows: item.item_modifier_groups ?? [],
    pricingCfg,
  })

  // Misma fórmula que el subtotal del carrito (el `cartTotal` de
  // RestaurantMicrosite.tsx y el subtotal `useMemo` de
  // components/microsite/sheet/OrderSheet.tsx): base a precio tarjeta +
  // deltas, que NUNCA redondean.
  const cardPrice = effectiveItemCardPrice(item.price, pricingCfg)
  const total = (cardPrice + flow.modifiersSubtotal) * flow.state.quantity

  const isLast = flow.state.index === flow.steps.length - 1
  const noteOpen = flow.state.note.length > 0 || noteExpanded

  /**
   * La nota es del PEDIDO, no del paso de extras — y desde que el extras vacío ya no
   * se emite, el último paso puede ser una pregunta. Si la nota siguiera colgada del
   * extras, un ítem cuyos grupos son todos preguntas se quedaría sin dónde escribirla.
   */
  const noteBlock = (
    <div className="px-5 pt-6 pb-2">
      {!noteOpen ? (
        <button
          type="button"
          onClick={() => setNoteExpanded(true)}
          className="text-sm font-semibold"
          style={{ color: accentColor }}
        >
          {t('addNote')}
        </button>
      ) : (
        <>
          <textarea
            value={flow.state.note}
            onChange={(e) => flow.dispatch({ type: 'setNote', value: e.target.value })}
            placeholder={t('notePlaceholder')}
            rows={2}
            className="w-full rounded-2xl border border-gray-200 px-3.5 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
          />
          <div className="flex justify-end">
            <span className="text-[11px] text-gray-400">{flow.state.note.length}/{MAX_NOTE_LEN}</span>
          </div>
        </>
      )}
    </div>
  )
  const showProgress = flow.questionCount > 0

  function handleCommit() {
    // Último candado antes de cobrar: `canAdvance` del paso `extras` es
    // SIEMPRE true, así que no alcanza para atrapar una pregunta obligatoria
    // que quedó desmarcada por un `goTo` hacia atrás y para adelante (ver
    // allQuestionsSatisfied). Sin esto, la línea entra al carrito con un
    // grupo required vacío — exactamente lo que ItemModal.validate() bloquea.
    if (!allQuestionsSatisfied(flow.steps, flow.state)) {
      const idx = firstUnsatisfiedQuestionIndex(flow.steps, flow.state)
      if (idx >= 0) flow.dispatch({ type: 'goTo', index: idx })
      return
    }
    onAdd(
      buildMicrositeCartLine({
        item,
        steps: flow.steps,
        selections: flow.state.selections,
        note: flow.state.note,
        quantity: flow.state.quantity,
        // eslint-disable-next-line react-hooks/purity
        cartId: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        pricingCfg,
      })
    )
  }

  const anyOptionalSelected = useMemo(() => {
    const last = flow.steps[flow.steps.length - 1]
    if (last?.kind !== 'extras') return false
    const pool = [...last.add, ...last.remove, ...last.choiceGroups.flatMap((c) => c.options)]
    return (
      pool.some((o) => (flow.state.selections[o.groupId]?.[o.optionId] ?? 0) > 0) ||
      flow.state.note.trim().length > 0
    )
  }, [flow.steps, flow.state.selections, flow.state.note])

  const photoOf = (o: ExtraOption) => o.photoUrl

  function optionList(options: ExtraOption[]) {
    const showPhotos = groupShowsPhotos(options, photoOf)
    return orderOptionsByPhoto(options, photoOf).map((o) => withLowStockTag(o.optionId, !o.soldOut && lowStock.option(o.optionId), optionChip(o, showPhotos)))
  }

  function optionChip(o: ExtraOption, showPhotos: boolean) {
    const qty = flow.state.selections[o.groupId]?.[o.optionId] ?? 0
    const selected = qty > 0

    // 86: el ingrediente se acabó. Se muestra apagado con "Agotado" en vez de
    // desaparecer — el cliente ve que hoy no hay, no que el local dejó de
    // ofrecerlo. Sin onClick ni stepper: /session también lo rechaza antes de
    // cobrar (lib/online-orders/pricing.ts, 409 modifiers_unavailable), pero
    // enterarse ahí obligaría al cliente a rearmar el plato en el pago.
    if (o.soldOut) {
      return (
        <div
          key={o.optionId}
          aria-disabled="true"
          className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-gray-200 bg-gray-50"
        >
          <span className="text-[15px] text-gray-400 line-through">{o.name}</span>
          <span className="ml-auto text-[11px] font-bold uppercase tracking-wide text-gray-500">
            {t('soldOut')}
          </span>
        </div>
      )
    }

    // Repetible (extra pagado o grupo de conteo requerido): un solo toque no
    // alcanza para bajar la cantidad, así que hace falta el mismo stepper
    // −/+ que ya tiene el kiosko — tanto acá como en el paso Agregar, para
    // que un "Queso extra +$1.13" tocado de más se pueda deshacer sin cerrar
    // la hoja y perder las respuestas ya dadas.
    if (o.repeatable) {
      return (
        <div
          key={o.optionId}
          className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border transition ${
            selected ? 'font-semibold' : 'border-gray-200'
          }`}
          style={selected ? { borderColor: accentColor, backgroundColor: `${accentColor}12`, color: accentColor } : undefined}
        >
          {showPhotos &&
            (o.photoUrl ? (
              <img
                src={menuPhotoUrl(o.photoUrl, 160)!}
                alt=""
                loading="lazy"
                className="w-16 h-16 rounded-[10px] object-cover flex-none"
              />
            ) : (
              // Cuadro vacío del mismo tamaño: mantiene alineada la columna de
              // nombres cuando el catálogo cubre solo a algunas del grupo.
              <span className="w-16 h-16 rounded-[10px] bg-gray-100 flex-none" aria-hidden="true" />
            ))}
          <span className="text-[15px]">{o.name}</span>
          {o.priceDelta !== 0 && (
            <span className="text-sm text-gray-500">
              {o.priceDelta > 0 ? '+' : ''}${Math.abs(o.priceDelta).toFixed(2)}
            </span>
          )}
          <span className="ml-auto flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => flow.dispatch({ type: 'changeQty', groupId: o.groupId, optionId: o.optionId, delta: -1 })}
              disabled={qty === 0}
              aria-label={`− ${o.name}`}
              className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-bold disabled:opacity-40"
            >
              −
            </button>
            <span className="w-4 text-center text-sm font-bold">{qty}</span>
            <button
              type="button"
              onClick={() => flow.dispatch({ type: 'changeQty', groupId: o.groupId, optionId: o.optionId, delta: 1 })}
              disabled={qty >= o.maxQty}
              aria-label={`+ ${o.name}`}
              className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 font-bold disabled:opacity-40"
            >
              +
            </button>
          </span>
        </div>
      )
    }

    return (
      <button
        key={o.optionId}
        type="button"
        onClick={() => flow.dispatch({ type: 'select', groupId: o.groupId, optionId: o.optionId })}
        className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border text-left transition ${
          selected ? 'font-semibold' : 'border-gray-200 hover:border-gray-300'
        }`}
        style={selected ? { borderColor: accentColor, backgroundColor: `${accentColor}12`, color: accentColor } : undefined}
      >
        {showPhotos &&
          (o.photoUrl ? (
            <img
              src={menuPhotoUrl(o.photoUrl, 160)!}
              alt=""
              loading="lazy"
              className="w-16 h-16 rounded-[10px] object-cover flex-none"
            />
          ) : (
            // Cuadro vacío del mismo tamaño: mantiene alineada la columna de
            // nombres cuando el catálogo cubre solo a algunas del grupo.
            <span className="w-16 h-16 rounded-[10px] bg-gray-100 flex-none" aria-hidden="true" />
          ))}
        <span className="text-[15px]">{o.name}</span>
        {o.priceDelta !== 0 && (
          <span className="ml-auto text-sm text-gray-500">
            {o.priceDelta > 0 ? '+' : ''}${Math.abs(o.priceDelta).toFixed(2)}
          </span>
        )}
      </button>
    )
  }

  function blockLabel(text: string) {
    return (
      <div className="flex items-center gap-2.5 pt-5 pb-2.5">
        <span className="text-[10.5px] font-bold tracking-[0.1em] uppercase text-gray-400">{text}</span>
        <span className="flex-1 h-px bg-gray-100" />
      </div>
    )
  }

  function renderQuestion(step: GuidedQuestionStep) {
    const used = Object.values(flow.state.selections[step.group.id] ?? {}).reduce((s, q) => s + q, 0)
    return (
      <div className="px-5">
        <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">{localizedGroupLabel(step.group, locale)}</h2>
        <p className="text-sm text-gray-500 mt-1.5">
          {step.min > 1 ? t('chooseCount', { min: step.min, used }) : t('required')}
        </p>
        <div className="mt-5 space-y-2.5">{optionList(step.options)}</div>
      </div>
    )
  }

  function renderExtras(step: GuidedExtrasStep) {
    // Mismo criterio que el kiosko (#2582): el título es el rótulo del dueño solo
    // si todo el paso sale de UN grupo con display_name propio; si no, «¿Algo
    // más?». «Tu {plato}» y el subtítulo nombran el plato para que un topping que
    // se llama igual que otra empanada («Carne Mechada») no se lea como otro producto.
    const groups = (item.item_modifier_groups ?? []).flatMap((r) => (r.modifier_groups ? [r.modifier_groups] : []))
    const title = guidedExtrasTitle(step, groups, locale) ?? t('extrasTitle')
    return (
      <div className="px-5">
        <p className="text-xs font-extrabold uppercase tracking-[0.06em] mb-1" style={{ color: accentColor }}>
          {t('buildingItem', { item: display.name })}
        </p>
        <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">{title}</h2>
        <p className="text-sm text-gray-500 mt-1.5">{t('extrasForItem', { item: display.name })}</p>

        {step.choiceGroups.map((cg) => (
          <div key={cg.group.id}>
            {blockLabel(localizedGroupLabel(cg.group, locale))}
            <div className="space-y-2.5">{optionList(cg.options)}</div>
          </div>
        ))}

        {step.add.length > 0 && (
          <>
            {blockLabel(t('blockAdd'))}
            <div className="space-y-2.5">{optionList(step.add)}</div>
          </>
        )}

        {step.remove.length > 0 && (
          <>
            {blockLabel(t('blockRemove'))}
            <div className="flex flex-wrap gap-2">
              {step.remove.map((o) => {
                const on = (flow.state.selections[o.groupId]?.[o.optionId] ?? 0) > 0
                return (
                  <button
                    key={o.optionId}
                    type="button"
                    onClick={() => flow.dispatch({ type: 'select', groupId: o.groupId, optionId: o.optionId })}
                    className={`px-3.5 py-2 rounded-full text-sm border transition ${
                      on ? 'border-gray-400 bg-gray-100 text-gray-500 line-through font-semibold' : 'border-gray-200 text-gray-700'
                    }`}
                  >
                    {o.name}
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <SheetPortal>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        // text-gray-900 NO es opcional: esta pantalla blanca vive dentro del
        // wrapper del tema (color: var(--ms-text)); sin él, cualquier texto sin
        // clase de color hereda el marfil de los temas oscuros y desaparece.
        className="fixed inset-0 z-50 flex justify-center bg-white text-gray-900 sm:items-center sm:bg-black/60 sm:p-6"
      >
        {/* El panel se separa de la capa fija a propósito. Antes la capa ERA el
            panel (`fixed inset-0` + `flex-col`), así que en escritorio el hero
            `aspect-[16/9]` ocupaba todo el ancho de la ventana — ~1070px de alto
            en una pantalla de 1900 — y al ser `flex-shrink-0` se negaba a
            encogerse: el área de contenido colapsaba a cero y el pie quedaba
            fuera de la pantalla. En móvil no se veía porque a 380px de ancho ese
            mismo hero mide 214px. Móvil sigue a pantalla completa; escritorio es
            una tarjeta centrada. */}
        <div className="relative flex h-full w-full flex-col bg-white sm:h-auto sm:max-h-[90dvh] sm:max-w-2xl sm:overflow-hidden sm:rounded-3xl sm:shadow-2xl">
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          {flow.state.index > 0 && (
            <button
              type="button"
              onClick={() => flow.dispatch({ type: 'back' })}
              aria-label={t('back')}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600"
            >
              ←
            </button>
          )}
          {showProgress && (
            <>
              <div className="flex-1 flex gap-1.5">
                {flow.steps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => flow.dispatch({ type: 'goTo', index: i })}
                    disabled={i > flow.state.maxReached}
                    aria-label={t('stepOf', { current: i + 1, total: flow.steps.length })}
                    className="flex-1 h-1 rounded-full border-0 p-0"
                    style={{ backgroundColor: i <= flow.state.index ? accentColor : '#ece6e0' }}
                  />
                ))}
              </div>
              <span className="text-[11px] text-gray-400 whitespace-nowrap">
                {t('stepOf', { current: flow.state.index + 1, total: flow.steps.length })}
              </span>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('close')}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {flow.state.index === 0 && item.image_url && (
          <div className="relative w-full aspect-[16/9] max-h-[30vh] bg-gray-100 flex-shrink-0 sm:max-h-64">
            <Image
              src={menuPhotoUrl(item.image_url, 900)}
              alt={display.name}
              fill
              sizes="(max-width: 768px) 100vw, 768px"
              className="object-cover"
              style={{ objectPosition: focalPosition(item.image_focal) }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <p className="absolute left-5 bottom-3 text-white text-xl font-extrabold">{display.name}</p>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto pt-4">
          {flow.current.kind === 'question' ? renderQuestion(flow.current) : renderExtras(flow.current)}
          {isLast && noteBlock}
        </div>

        <div
          className="flex-shrink-0 px-4 pt-3 border-t border-gray-100 bg-white flex items-center gap-3"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          {isLast && (
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => flow.dispatch({ type: 'setQuantity', value: flow.state.quantity - 1 })}
                disabled={flow.state.quantity <= 1}
                aria-label={t('quantity')}
                className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-bold disabled:opacity-50"
              >
                −
              </button>
              <span className="w-5 text-center font-semibold">{flow.state.quantity}</span>
              <button
                type="button"
                onClick={() => flow.dispatch({ type: 'setQuantity', value: flow.state.quantity + 1 })}
                className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-bold"
              >
                +
              </button>
            </div>
          )}
          {isLast && flow.current.kind === 'extras' && !anyOptionalSelected && (
            <button
              type="button"
              onClick={handleCommit}
              className="rounded-full py-3.5 px-5 text-sm font-bold border border-gray-300 text-gray-700"
            >
              {t('skip')}
            </button>
          )}
          <button
            type="button"
            onClick={isLast ? handleCommit : () => flow.dispatch({ type: 'next' })}
            disabled={!flow.canAdvance}
            className="flex-1 rounded-full py-4 text-white text-base font-bold transition disabled:opacity-40"
            style={{ backgroundColor: accentColor }}
          >
            {isLast ? t('addToCart', { price: `$${total.toFixed(2)}` }) : t('next')}
          </button>
        </div>
        </div>
      </motion.div>
    </SheetPortal>
  )
}
